import { useEffect, useState } from 'preact/hooks'
import { useOption } from '../hooks/useOption'
import { RANGE_INDICATOR, parseActiveRange } from './FilterUtils'

const isRealRange = (range) => Array.isArray(range) && range.length >= 2 && (range[0] !== 0 || range[1] !== 0)

function rangeFromValues(values) {
  if (!values?.length) return null

  const nums = values.flatMap((item) => {
    if (item.type === 'range' || item.type === 'slider') {
      return [parseFloat(item.low), parseFloat(item.high)].filter((n) => !Number.isNaN(n))
    }
    const n = parseFloat(item.value ?? item.label)
    return Number.isNaN(n) ? [] : [n]
  })

  if (!nums.length) return null
  return [Math.min(...nums), Math.max(...nums)]
}

export function getSliderBounds(filter, fetchedFilter) {
  if (isRealRange(filter?.range)) {
    return { min: filter.range[0], max: filter.range[1] }
  }

  if (isRealRange(fetchedFilter?.range)) {
    return { min: fetchedFilter.range[0], max: fetchedFilter.range[1] }
  }

  const fromValues = rangeFromValues(filter?.values) || rangeFromValues(fetchedFilter?.values)
  if (fromValues) return { min: fromValues[0], max: fromValues[1] }

  return null
}

export function FilterInputSlider({ filter, fetchedFilter, onFilter, onRemove }) {
  const bounds = getSliderBounds(filter, fetchedFilter)
  const activeFilters = useOption('filters', {
    defaultValue: new Set()
  })
  const activeRange = parseActiveRange(filter.field, activeFilters)

  if (!bounds) return null

  const step = Math.max(Math.floor(filter.step ?? 1), 1)

  function handleSelect(value) {
    if (value.min <= bounds.min && value.max >= bounds.max) {
      onRemove?.()
      return
    }

    onFilter(`${filter.field}:${value.min}${RANGE_INDICATOR}${value.max}`)
  }

  return (
    <div class="flex flex-col gap-5">
      <div class="flex items-center justify-between">
        <p class="paragraph-sm">Min <span class="text-neutrals-secondary-grey">${bounds.min}</span></p>
        <p class="paragraph-sm">Max <span class="text-neutrals-secondary-grey">${bounds.max}</span></p>
      </div>
      <RangeSlider
        min={bounds.min}
        max={bounds.max}
        step={step}
        activeRange={activeRange}
        onChange={handleSelect}
      />
    </div>
  )
}

// Credit here:
// https://benhoneywill.com/building-a-range-slider-component-in-react/

const RangeSlider = ({ min, max, step, onChange, activeRange }) => {
  const [minValue, setMinValue] = useState(activeRange ? activeRange[0] : min)
  const [maxValue, setMaxValue] = useState(activeRange ? activeRange[1] : max)

  useEffect(() => {
    if (activeRange) {
      setMinValue(activeRange[0])
      setMaxValue(activeRange[1])
      return
    }

    setMinValue(min)
    setMaxValue(max)
  }, [min, max, activeRange?.[0], activeRange?.[1]])

  const handleMinChange = (event) => {
    const newMinVal = Math.min(+event.target.value, maxValue - step)
    setMinValue(newMinVal)
    onChange({ min: newMinVal, max: maxValue })
  }

  const handleMaxChange = (event) => {
    const newMaxVal = Math.max(+event.target.value, minValue + step)
    setMaxValue(newMaxVal)
    onChange({ min: minValue, max: newMaxVal })
  }

  const minPos = ((minValue - min) / (max - min)) * 100
  const maxPos = ((maxValue - min) / (max - min)) * 100

  return (
    <div class="relative flex h-4 items-center">
      <div class="absolute h-4 w-full">
        <input
          class="range pointer-events-none absolute top-1/2 z-20 h-full w-full -translate-y-1/2 appearance-none p-0 opacity-0"
          type="range"
          value={minValue}
          min={min}
          max={max}
          step={step}
          onChange={handleMinChange}
          aria-label="Min"
        />
        <input
          class="range pointer-events-none absolute top-1/2 z-20 h-full w-full -translate-y-1/2 appearance-none p-0 opacity-0"
          type="range"
          value={maxValue}
          min={min}
          max={max}
          step={step}
          onChange={handleMaxChange}
          aria-label="Max"
        />
      </div>
      <div class="pointer-events-none absolute h-4 w-full">
        <div
          class="absolute top-1/2 z-10 h-4 w-4 -translate-y-1/2 rounded-full bg-primary-black"
          style={{ left: `${minPos}%` }}
        />
        <div class="absolute top-1/2 h-0.5 w-full -translate-y-1/2 bg-neutrals-light-grey">
          <div class="absolute h-full bg-primary-black" style={{ left: `${minPos}%`, right: `${100 - maxPos}%` }} />
        </div>
        <div
          class="absolute top-1/2 z-10 h-4 w-4 -translate-x-full -translate-y-1/2 rounded-full bg-primary-black"
          style={{ left: `${maxPos}%` }}
        />
      </div>
    </div>
  )
}
